import { Check, Circle } from "lucide-react";
import {
  ORDER_PROGRESS_STAGES,
  ORDER_STATUS_LABELS,
  type OrderStatus,
  type StatusHistoryEntry,
} from "@printtat/shared";

interface StatusTimelineProps {
  currentStatus: OrderStatus;
  statusHistory: StatusHistoryEntry[];
}

const getStageState = (
  stage: OrderStatus,
  currentStatus: OrderStatus
): "completed" | "current" | "upcoming" => {
  if (currentStatus === "cancelled" || currentStatus === "returned") {
    const historyStages = ORDER_PROGRESS_STAGES;
    const stageIndex = historyStages.indexOf(stage);
    const currentIndex = historyStages.indexOf(currentStatus);
    if (currentIndex === -1) {
      // Terminal state — mark stages before last normal status as completed
      return "upcoming";
    }
    if (stageIndex < currentIndex) return "completed";
    if (stageIndex === currentIndex) return "current";
    return "upcoming";
  }

  const stageIndex = ORDER_PROGRESS_STAGES.indexOf(stage);
  const currentIndex = ORDER_PROGRESS_STAGES.indexOf(currentStatus);

  if (stageIndex < currentIndex) return "completed";
  if (stageIndex === currentIndex) return "current";
  return "upcoming";
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-EG", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const StatusTimeline = ({
  currentStatus,
  statusHistory,
}: StatusTimelineProps) => {
  const isCancelled = currentStatus === "cancelled";
  const isReturned = currentStatus === "returned";
  const isTerminal = isCancelled || isReturned;

  // Find timestamp for each stage from history
  const stageTimestamps = new Map<string, string>();
  for (const entry of statusHistory) {
    if (!stageTimestamps.has(entry.status)) {
      stageTimestamps.set(entry.status, entry.timestamp);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-6">
        Order Progress
      </h3>

      <div className="relative">
        {ORDER_PROGRESS_STAGES.map((stage, index) => {
          const state = getStageState(stage, currentStatus);
          const timestamp = stageTimestamps.get(stage);
          const isLast = index === ORDER_PROGRESS_STAGES.length - 1;

          return (
            <div key={stage} className="flex gap-3 relative">
              {/* Connector line */}
              {!isLast && (
                <div
                  className={`absolute left-[11px] top-[24px] w-0.5 h-full ${
                    state === "completed" || state === "current" ? "bg-gray-900" : "bg-gray-200"
                  }`}
                />
              )}

              {/* Status dot */}
              <div className="relative z-10 flex-shrink-0 mt-0.5">
                {state === "completed" || (state === "current" && stage === "delivered") ? (
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${stage === "delivered" && state === "current" ? "bg-green-600" : "bg-gray-900"}`}>
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                ) : state === "current" ? (
                  <div className="w-6 h-6 rounded-full border-2 border-gray-900 bg-white flex items-center justify-center">
                    <Circle className="w-2.5 h-2.5 fill-gray-900 text-gray-900" />
                  </div>
                ) : (
                  <div className="w-6 h-6 rounded-full border-2 border-gray-200 bg-white" />
                )}
              </div>

              {/* Content */}
              <div className={`pb-6 pt-1 ${isLast ? "pb-0" : ""}`}>
                <p
                  className={`text-sm font-medium ${
                    stage === "delivered" && state === "current" ? "text-green-600" : state === "upcoming" ? "text-gray-400" : "text-gray-900"
                  }`}
                >
                  {ORDER_STATUS_LABELS[stage]}
                </p>
                {timestamp && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {formatDate(timestamp)}
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {/* Terminal state indicator */}
        {isTerminal && (
          <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
            <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Circle className="w-2.5 h-2.5 fill-red-500 text-red-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-red-600">
                {ORDER_STATUS_LABELS[currentStatus]}
              </p>
              {stageTimestamps.get(currentStatus) && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {formatDate(stageTimestamps.get(currentStatus)!)}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
